import { create } from "zustand"
import { devtools } from "zustand/middleware"

import type { Invoice, Otherpromo, PaymentOption } from "@be-miniapp/api"
import {
  PaymentOptionKey,
  type DriverAcceptedData,
  type FullEngagementInfo,
  type Location,
  type RegionExtended,
  type SSERideStatus,
  type TripState,
} from "@be-miniapp/types"

interface RideLocation {
  type: "pick-up" | "drop-off" | "user-location" | "save-address"
  id?: string
  label?: string
  name?: string
  address?: string
  placeId?: string
  location?: Location
  distance?: string
  isConfirmed?: boolean
  metadata?: string
}

interface RouteSelectionState {
  pickup?: RideLocation
  dropoff?: RideLocation
  savedLoc?: RideLocation
}

interface RouteSelectionActions {
  setPickup: (pickup: RideLocation | undefined) => void
  setDropoff: (dropoff: RideLocation | undefined) => void
  setSavedLoc: (savedLoc: RideLocation | undefined) => void
}

interface BookingState {
  vehicleType: number
  paymentMethod?: PaymentOption
  preferredPaymentMethod?: number
  currentPromo?: Otherpromo
  isCurrentPromoTouched: boolean
  isFromBestCoupon: boolean
  note?: string
  invoice?: Invoice
}

interface BookingActions {
  setVehicleType: (vehicleType: number) => void
  setPreferredPaymentMethod: (preferredPaymentMethod: number) => void
  setCurrentPromo: (
    currentPromo: Otherpromo | undefined,
    isCurrentPromoTouched?: boolean,
    isFromBestCoupon?: boolean
  ) => void
  setPaymentMethod: (paymentMethod: PaymentOption) => void
  setNote: (note: string | undefined) => void
  setInvoice: (invoice?: Invoice) => void
}

interface LiveSessionState {
  sessionId?: number
  lastMsgId?: string | null
  engagement?: DriverAcceptedData & FullEngagementInfo
  engagements: Record<
    string,
    (DriverAcceptedData & FullEngagementInfo) | undefined
  >
  extendedEngagement?: FullEngagementInfo
  regions?: RegionExtended[]
  state?: TripState
  latestSSEEvent?: SSERideStatus
}

interface HydrateInTripSessionParams {
  sessionId: number
  pickup: RideLocation
  dropoff: RideLocation
  engagement?: DriverAcceptedData | FullEngagementInfo
  state: TripState
  paymentMethod?: PaymentOption
  preferredPaymentMethod?: number
}

interface LiveSessionActions {
  setSessionId: (sessionId: number) => void
  setEngagement: (
    sessionData: DriverAcceptedData | FullEngagementInfo,
    reset?: boolean
  ) => void
  setExtendedEngagement: (sessionData: FullEngagementInfo) => void
  setRegions: (regions: RegionExtended[]) => void
  setCurrentState: (state: TripState | undefined) => void
  setLatestSSEEvent: (latestSSEEvent: SSERideStatus) => void
  setLastMsgId: (lastMsgId: string | null) => void
  clearEngagement: () => void
  hydrateInTripSession: (params: HydrateInTripSessionParams) => void
  resetRideState: () => void
  setEngagementByID: (
    sessionData: DriverAcceptedData | FullEngagementInfo,
    reset?: boolean
  ) => void
  getEngagementByID: (
    engagementId: number
  ) => (DriverAcceptedData & FullEngagementInfo) | undefined
}

interface StoreActions {
  reset: () => void
}

interface CurrentRideData
  extends RouteSelectionState,
    BookingState,
    LiveSessionState,
    RouteSelectionActions,
    BookingActions,
    LiveSessionActions,
    StoreActions {}

interface CreateRideDataStoreParams {
  getUserLocation: () => {
    latitude: number | undefined
    longitude: number | undefined
  }
  normalizeDropoffFromPlaceId?: boolean
  allowUndefinedDropoffLocation?: boolean
  resetBehavior?: "merge" | "replace"
}

function getDropoffLocation({
  getUserLocation,
  allowUndefinedDropoffLocation,
}: {
  getUserLocation: CreateRideDataStoreParams["getUserLocation"]
  allowUndefinedDropoffLocation: boolean
}): Location | undefined {
  const userLocation = getUserLocation()

  if (
    allowUndefinedDropoffLocation &&
    (userLocation.latitude === undefined ||
      userLocation.longitude === undefined)
  ) {
    return undefined
  }

  if (
    userLocation.latitude === undefined ||
    userLocation.longitude === undefined
  ) {
    return undefined
  }

  return {
    latitude: userLocation.latitude,
    longitude: userLocation.longitude,
  }
}

function createRideDataStore({
  getUserLocation,
  normalizeDropoffFromPlaceId = false,
  allowUndefinedDropoffLocation = false,
  resetBehavior = "replace",
}: CreateRideDataStoreParams) {
  const initialRideData: Omit<
    CurrentRideData,
    | keyof RouteSelectionActions
    | keyof BookingActions
    | keyof LiveSessionActions
    | "reset"
  > = {
    vehicleType: 0,
    dropoff: {
      type: "drop-off",
      location: getDropoffLocation({
        getUserLocation,
        allowUndefinedDropoffLocation,
      }),
    },
    sessionId: undefined,
    engagements: {},
    preferredPaymentMethod: PaymentOptionKey.CASH,
    pickup: undefined,
    note: "",
    invoice: undefined,
    currentPromo: undefined,
    isCurrentPromoTouched: false,
    isFromBestCoupon: true,
  }

  return create<CurrentRideData, [["zustand/devtools", CurrentRideData]]>(
    devtools((set, get) => ({
      ...initialRideData,
      setPickup: (pickup: RideLocation | undefined) => set({ pickup }),
      setDropoff: (dropoff: RideLocation | undefined) =>
        set({
          dropoff: dropoff
            ? {
                ...dropoff,
                ...(normalizeDropoffFromPlaceId
                  ? { isConfirmed: !!dropoff.placeId }
                  : {}),
              }
            : {
                type: "drop-off",
                name: "",
                location: getDropoffLocation({
                  getUserLocation,
                  allowUndefinedDropoffLocation,
                }),
              },
        }),
      setSavedLoc: (savedLoc: RideLocation | undefined) => set({ savedLoc }),
      setVehicleType: (vehicleType: number) => set({ vehicleType }),
      setSessionId: (sessionId: number) => set({ sessionId }),
      setEngagement: (
        sessionData: DriverAcceptedData | FullEngagementInfo,
        reset: boolean = false
      ) =>
        reset
          ? set({ engagement: undefined })
          : set({
              engagement: (get().engagement
                ? { ...get().engagement, ...sessionData }
                : sessionData) as DriverAcceptedData & FullEngagementInfo,
            }),
      setPreferredPaymentMethod: (preferredPaymentMethod: number) =>
        set({ preferredPaymentMethod }),
      setCurrentPromo: (
        currentPromo: Otherpromo | undefined,
        isCurrentPromoTouched: boolean = false,
        isFromBestCoupon: boolean = true
      ) => set({ currentPromo, isCurrentPromoTouched, isFromBestCoupon }),
      setRegions: (regions: RegionExtended[]) => set({ regions }),
      setPaymentMethod: (paymentMethod: PaymentOption) =>
        set({ paymentMethod }),
      setExtendedEngagement: (extendedEngagement: FullEngagementInfo) => {
        set({ extendedEngagement })
      },
      setCurrentState: (state: TripState | undefined) => set({ state }),
      setLatestSSEEvent: (latestSSEEvent: SSERideStatus) =>
        set({ latestSSEEvent }),
      setNote: (note: string | undefined) => set({ note }),
      setInvoice: (invoice?: Invoice) => set({ invoice }),
      setLastMsgId: (lastMsgId: string | null) => set({ lastMsgId }),
      clearEngagement: () => set({ engagement: undefined, engagements: {} }),
      hydrateInTripSession: ({
        sessionId,
        pickup,
        dropoff,
        engagement,
        state,
        paymentMethod,
        preferredPaymentMethod,
      }: HydrateInTripSessionParams) =>
        set({
          sessionId,
          pickup,
          dropoff,
          engagement: engagement as
            | (DriverAcceptedData & FullEngagementInfo)
            | undefined,
          engagements: {},
          extendedEngagement: undefined,
          lastMsgId: null,
          latestSSEEvent: undefined,
          state,
          paymentMethod,
          preferredPaymentMethod,
        }),
      reset: () => {
        if (resetBehavior === "merge") {
          set((prev) => ({ ...prev, ...initialRideData }))
          return
        }

        set({ ...initialRideData })
      },
      resetRideState: () =>
        set({
          state: undefined,
          engagement: undefined,
          engagements: {},
          extendedEngagement: undefined,
          sessionId: undefined,
          lastMsgId: null,
          latestSSEEvent: undefined,
        }),
      setEngagementByID: (
        sessionData: DriverAcceptedData | FullEngagementInfo,
        reset?: boolean
      ) =>
        reset
          ? set({
              engagements: {
                ...get().engagements,
                [sessionData.engagement_id]: undefined,
              },
            })
          : set({
              engagements: {
                ...get().engagements,
                [sessionData.engagement_id]: {
                  ...get().engagements[sessionData.engagement_id],
                  ...sessionData,
                },
              },
            }),
      getEngagementByID: (id: number) => get().engagements[id.toString()],
    }))
  )
}

export { createRideDataStore }
export type { CurrentRideData, HydrateInTripSessionParams, RideLocation }
